import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import MainLayout from '../../components/layout/MainLayout';
import companyApi, { CompanySettings, DataStatistics } from '../../services/api/company.api';
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CircleStackIcon,
  GlobeAltIcon,
  FilmIcon,
  CpuChipIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  DevicePhoneMobileIcon,
  ServerIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

export default function CompanyPage() {
  const { t } = useTranslation(['settings', 'common']);
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [statistics, setStatistics] = useState<DataStatistics | null>(null);
  const [retentionDays, setRetentionDays] = useState(30);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [settingsRes, statsRes] = await Promise.all([
        companyApi.getSettings(),
        companyApi.getDataStatistics(),
      ]);

      if (settingsRes.success) {
        setSettings(settingsRes.data);
        setRetentionDays(settingsRes.data.dataRetentionDays);
      }

      if (statsRes.success) {
        setStatistics(statsRes.data);
      }
    } catch (error) {
      console.error('Failed to load company data:', error);
      toast.error(t('common:messages.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveRetention = async () => {
    setIsSaving(true);
    try {
      const response = await companyApi.updateSettings(retentionDays);
      if (response.success) {
        setSettings(response.data);
        toast.success(t('settings:company.retention.saved'));
      } else {
        toast.error(response.message || t('common:messages.error'));
      }
    } catch (error: any) {
      console.error('Failed to update retention:', error);
      toast.error(error.response?.data?.error || t('common:messages.error'));
    } finally {
      setIsSaving(false);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/settings')}
          className="flex items-center text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white mb-6 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          {t('common:buttons.back')}
        </button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t('settings:company.title')}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {t('settings:company.subtitle')}
          </p>
        </div>

        {/* Data Retention Configuration */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex items-center mb-4">
            <ClockIcon className="h-6 w-6 text-indigo-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('settings:company.retention.title')}
            </h2>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('settings:company.retention.description')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('settings:company.retention.retentionPeriod')}
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min={7}
                  max={settings?.maxRetentionDays || 30}
                  value={retentionDays}
                  onChange={(e) => setRetentionDays(parseInt(e.target.value))}
                  className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                />
                <div className="w-24">
                  <input
                    type="number"
                    min={7}
                    max={settings?.maxRetentionDays || 30}
                    value={retentionDays}
                    onChange={(e) => setRetentionDays(Math.max(7, Math.min(settings?.maxRetentionDays || 30, parseInt(e.target.value) || 7)))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-center dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <span className="text-gray-600 dark:text-gray-400">
                  {t('settings:company.retention.days')}
                </span>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {t('settings:company.retention.currentPlan')}
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {settings?.planName || 'Free'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {t('settings:company.retention.maxRetention')}
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {settings?.maxRetentionDays || 30} {t('settings:company.retention.days')}
                </span>
              </div>
              {settings && retentionDays >= settings.maxRetentionDays && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                  {t('settings:company.retention.limitedByPlan')}
                </p>
              )}
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSaveRetention}
              disabled={isSaving || retentionDays === settings?.dataRetentionDays}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSaving && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
              {isSaving ? t('common:buttons.saving') : t('common:buttons.save')}
            </button>
          </div>
        </div>

        {/* Data Storage Statistics */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <ChartBarIcon className="h-6 w-6 text-indigo-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('settings:company.statistics.title')}
              </h2>
            </div>
            <button
              onClick={loadData}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white"
            >
              <ArrowPathIcon className="h-5 w-5" />
            </button>
          </div>

          {statistics && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4">
                  <p className="text-sm text-indigo-600 dark:text-indigo-400">
                    {t('settings:company.statistics.totalRecords')}
                  </p>
                  <p className="text-2xl font-bold text-indigo-900 dark:text-indigo-100">
                    {formatNumber(statistics.totalRecords)}
                  </p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                  <p className="text-sm text-green-600 dark:text-green-400">
                    {t('settings:company.statistics.storageEstimate')}
                  </p>
                  <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                    {statistics.storageEstimateGB} GB
                  </p>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                  <p className="text-sm text-purple-600 dark:text-purple-400">
                    {t('settings:company.statistics.recordings')}
                  </p>
                  <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                    {formatNumber(statistics.recordings)}
                  </p>
                </div>
              </div>

              {/* Detailed Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                  icon={<DocumentTextIcon className="h-5 w-5" />}
                  label={t('settings:company.statistics.logs')}
                  value={formatNumber(statistics.logs)}
                  color="blue"
                />
                <StatCard
                  icon={<ServerIcon className="h-5 w-5" />}
                  label={t('settings:company.statistics.traces')}
                  value={formatNumber(statistics.traces)}
                  color="purple"
                />
                <StatCard
                  icon={<CircleStackIcon className="h-5 w-5" />}
                  label={t('settings:company.statistics.spans')}
                  value={formatNumber(statistics.spans)}
                  color="indigo"
                />
                <StatCard
                  icon={<GlobeAltIcon className="h-5 w-5" />}
                  label={t('settings:company.statistics.rumSessions')}
                  value={formatNumber(statistics.rumSessions)}
                  color="green"
                />
                <StatCard
                  icon={<ChartBarIcon className="h-5 w-5" />}
                  label={t('settings:company.statistics.rumEvents')}
                  value={formatNumber(statistics.rumEvents)}
                  color="teal"
                />
                <StatCard
                  icon={<FilmIcon className="h-5 w-5" />}
                  label={t('settings:company.statistics.recordings')}
                  value={formatNumber(statistics.recordings)}
                  color="pink"
                />
                <StatCard
                  icon={<CpuChipIcon className="h-5 w-5" />}
                  label={t('settings:company.statistics.metrics')}
                  value={formatNumber(statistics.metricDataPoints)}
                  color="orange"
                />
                <StatCard
                  icon={<ClockIcon className="h-5 w-5" />}
                  label={t('settings:company.statistics.uptimeChecks')}
                  value={formatNumber(statistics.uptimeChecks)}
                  color="cyan"
                />
                <StatCard
                  icon={<ExclamationTriangleIcon className="h-5 w-5" />}
                  label={t('settings:company.statistics.errors')}
                  value={formatNumber(statistics.errorOccurrences)}
                  color="red"
                />
                <StatCard
                  icon={<DevicePhoneMobileIcon className="h-5 w-5" />}
                  label="Mobile Sessions"
                  value={formatNumber(statistics.mobileSessions)}
                  color="violet"
                />
                <StatCard
                  icon={<ServerIcon className="h-5 w-5" />}
                  label="Infra Metrics"
                  value={formatNumber(statistics.infrastructureMetrics)}
                  color="gray"
                />
                <StatCard
                  icon={<ExclamationTriangleIcon className="h-5 w-5" />}
                  label="Mobile Crashes"
                  value={formatNumber(statistics.mobileCrashes)}
                  color="amber"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  color: string;
}

function StatCard({ icon, label, value, color }: StatCardProps) {
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400',
    purple: 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400',
    indigo: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400',
    green: 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400',
    teal: 'bg-teal-50 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400',
    pink: 'bg-pink-50 text-pink-600 dark:bg-pink-900/20 dark:text-pink-400',
    orange: 'bg-orange-50 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400',
    cyan: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-900/20 dark:text-cyan-400',
    red: 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400',
    violet: 'bg-violet-50 text-violet-600 dark:bg-violet-900/20 dark:text-violet-400',
    gray: 'bg-gray-50 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
    amber: 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400',
  };

  return (
    <div className={`rounded-lg p-3 ${colorClasses[color] || colorClasses.gray}`}>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-xs font-medium opacity-80">{label}</span>
      </div>
      <p className="text-lg font-bold">{value}</p>
    </div>
  );
}
